import i18n from '../../../i18n';
import type { AnalysisResult, JobContext, ResumeData } from '../canvas/CanvasProvider';

function scoreLabelKey(score: number): string {
  if (score >= 70) return 'agent.workflow.scoreStrong';
  if (score >= 45) return 'agent.workflow.scoreModerate';
  return 'agent.workflow.scoreLow';
}

/** Localized analysis assistant message + action labels for the optimize flow. */
export function buildAnalysisMessage(
  job: JobContext,
  analysis: AnalysisResult,
  resumeData: ResumeData | null | undefined,
): { content: string; yesRewriteLabel: string; pickDifferentLabel: string } {
  const t = i18n.t.bind(i18n);
  const scoreLabel = t(scoreLabelKey(analysis.matchScore));

  const topMiss = analysis.keywordMisses[0];
  const prevCompany = resumeData?.experience?.[0]?.company ?? null;
  let gapMicroPrompt = '';
  if (topMiss && prevCompany) {
    gapMicroPrompt = `\n\n${t('agent.workflow.gapAtCompany', {
      skill: topMiss,
      company: prevCompany,
    })}`;
  } else if (analysis.keywordMisses.length > 0) {
    gapMicroPrompt = `\n\n${t('agent.workflow.missingAdjacent', {
      skills: analysis.keywordMisses.slice(0, 3).join(', '),
    })}`;
  }

  const matchedLine = analysis.keywordHits.length > 0
    ? `\n${t('agent.workflow.matched', { keywords: analysis.keywordHits.slice(0, 5).join(', ') })}`
    : '';
  const missingLine = analysis.keywordMisses.length > 0
    ? `\n${t('agent.workflow.toStrengthen', { keywords: analysis.keywordMisses.slice(0, 4).join(', ') })}`
    : '';

  const content = [
    t('agent.workflow.analyzed', { title: job.title, company: job.company }),
    '',
    t('agent.workflow.matchScore', { score: analysis.matchScore, label: scoreLabel }),
    matchedLine,
    missingLine,
    gapMicroPrompt,
    '',
    t('agent.workflow.rewriteQuestion'),
  ].join('\n');

  return {
    content,
    yesRewriteLabel: t('agent.workflow.yesRewrite'),
    pickDifferentLabel: t('agent.workflow.pickDifferentJob'),
  };
}

export function optimizeUserMessage(title: string, company: string): string {
  return i18n.t('agent.workflow.optimizeUserMessage', { title, company });
}

export function startingAnalysisMessage(title: string, company: string): string {
  return i18n.t('agent.workflow.startingAnalysis', { title, company });
}

/** Map known English workflow action labels to i18n keys (instant, no Groq). */
const KNOWN_ACTION_LABEL_KEYS: Record<string, string> = {
  'Yes, rewrite my resume': 'agent.workflow.yesRewrite',
  'Pick a different job': 'agent.workflow.pickDifferentJob',
  'Yes, write cover letter': 'agent.workflow.yesWriteCoverLetter',
  'Skip — Download PDF': 'agent.workflow.skipDownloadPdf',
  'Make a change': 'agent.workflow.makeAChange',
  'Yes, write a cover letter': 'agent.workflow.yesWriteCoverLetterAlt',
  'No, skip to download': 'agent.workflow.noSkipDownload',
  'Retry': 'agent.workflow.retry',
  'Add real experience': 'agent.workflow.addRealExperience',
  'Continue as-is': 'agent.workflow.continueAsIs',
  'Generate Application Note': 'agent.workflow.generateApplicationNote',
  'Refine this cover letter': 'agent.workflow.refineCoverLetter',
  'Download PDF': 'agent.workflow.downloadPdf',
  'Download Resume PDF': 'agent.workflow.downloadResumePdf',
  'Find similar jobs': 'agent.workflow.findSimilarJobs',
  'Write cover letter': 'agent.engagement.actions.writeCoverLetter',
  'Write application letter': 'agent.engagement.actions.writeApplicationLetter',
  'Interview prep': 'agent.engagement.actions.interviewPrep',
  'Track applications': 'agent.engagement.actions.trackApplications',
  'Dashboard': 'agent.engagement.actions.dashboard',
};

const APPLY_TO_RE = /^Apply to (.+)$/;

export function localizeActionLabel(label: string): string {
  const key = KNOWN_ACTION_LABEL_KEYS[label];
  if (key) return i18n.t(key, { defaultValue: label });
  const applyMatch = label.match(APPLY_TO_RE);
  if (applyMatch) {
    return i18n.t('agent.engagement.actions.applyTo', {
      defaultValue: 'Apply to {{company}}',
      company: applyMatch[1],
    });
  }
  return label;
}
